import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Reuse the existing current-plus-one pipeline at every viewport size.
    if (this.hasAttribute('data-desktop-image-lookahead')) {
      this.#setupDesktopImageLookahead(signal);
    }
  }

  #controller = new AbortController();
  /** @type {HTMLElement[]} Stable order, even while the carousel temporarily moves slides for a loop. */
  #preloadSlides = [];
  #preloadIndex = 0;
  #initialImageReady = false;
  /** @type {WeakSet<HTMLImageElement>} */
  #preparedImages = new WeakSet();

  /** @param {AbortSignal} signal */
  #setupDesktopImageLookahead(signal) {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    this.#preloadSlides = Array.from(slideshow.querySelectorAll('slideshow-slide[ref="slides[]"]'));
    this.#preloadIndex = Math.max(0, this.#preloadSlides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
    const initialImage = this.#preloadSlides[this.#preloadIndex]?.querySelector('img.product-media__image');
    this.#initialImageReady = !(initialImage instanceof HTMLImageElement) || initialImage.complete;
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    if (initialImage instanceof HTMLImageElement && !this.#initialImageReady) {
      initialImage.addEventListener('load', this.#handleInitialImageReady, { once: true, signal });
      initialImage.addEventListener('error', this.#handleInitialImageReady, { once: true, signal });
    }
    this.#prepareDesktopImages();
  }

  #handleInitialImageReady = () => {
    this.#initialImageReady = true;
    this.#prepareDesktopImages();
  };

  /** @param {SlideshowSelectEvent} event */
  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;
    const index = this.#preloadSlides.indexOf(/** @type {HTMLElement} */ (event.detail.slide));
    if (index < 0) return;
    // An explicit move must not wait for the previously visible image to finish.
    if (index !== this.#preloadIndex) this.#initialImageReady = true;
    this.#preloadIndex = index;
    this.#prepareDesktopImages();
  };

  #prepareDesktopImages = () => {
    if (!this.isConnected) return;
    const slides = this.#preloadSlides.filter((slide) => !slide.hidden || slide.hasAttribute('reveal'));
    const current = slides.indexOf(this.#preloadSlides[this.#preloadIndex]);
    if (current < 0) return;

    // Prioritize the first image, then warm exactly one ahead without waiting for unrelated page assets.
    // Only navigation advances this window; finishing a prefetched image must not start another.
    const count = Math.min(this.#initialImageReady ? 2 : 1, slides.length);
    for (let offset = 0; offset < count; offset++) {
      let index = current + offset;
      if (index >= slides.length) {
        if (!this.slideshow?.infinite) break;
        index %= slides.length;
      }
      const image = slides[index]?.querySelector('img.product-media__image');
      if (!(image instanceof HTMLImageElement) || this.#preparedImages.has(image)) continue;

      this.#preparedImages.add(image);
      // Reuse the rendered responsive source, never the much larger zoom asset.
      image.loading = 'eager';
      image.decode?.().catch(() => {
        // Loading/decoding failure must never prevent normal carousel navigation.
        this.#preparedImages.delete(image);
      });
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
